import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { TopNav } from '@/components/TopNav';
import { BottomNavigation } from '@/components/BottomNavigation';
import { HistoryDrawer } from '@/components/HistoryDrawer';
import { FaqDrawer } from '@/components/FaqDrawer';
import { PrizeTiersDrawer } from '@/components/PrizeTiersDrawer';
import { SettingsSheet } from '@/components/SettingsSheet';
import { EarnPointsSection } from '@/components/EarnPointsSection';
import { SolflareBonusInfoTooltip } from '@/components/SolflareBonusInfoTooltip';
import { MagicEdenBonusInfoTooltip } from '@/components/MagicEdenBonusInfoTooltip';
import { GateBonusInfoTooltip } from '@/components/GateBonusInfoTooltip';
import { usePointsLotterySeason } from '@/hooks/usePointsLottery';
import { useUserPoints } from '@/hooks/useUserPoints';
import { usePointsLedgerInfinite } from '@/hooks/usePointsLedger';
import {
  usePointsLeaderboard,
  useMyPointsRank,
} from '@/hooks/usePointsLeaderboard';
import { useResponsive } from '@/hooks/useResponsive';
import { useAuthContext } from '@/context/AuthContext';
import { useSolflareCampaign } from '@/hooks/useSolflareCampaign';
import { useMagicEdenCampaign } from '@/hooks/useMagicEdenCampaign';
import config from '@/config';
import { lamportsToSol } from '@/utils/solana/lamportsToSol';
import { formatCountdown } from '@/utils/formatCountdown';
import { AppRoutesEnum } from '@/lib/constants';
import type { TimeRange } from '@/api/pointsLeaderboard';
import type { RankingType } from '@/types/schemas';

function formatNumber(value: string | number | undefined) {
  if (value === undefined) return '0';
  const numeric = typeof value === 'string' ? Number(value) : value;
  return Number.isNaN(numeric) ? '0' : numeric.toLocaleString();
}

// Type label mappings for points ledger entries
const TYPE_LABELS: Record<string, string> = {
  chest_open: 'Open Chests',
  chest_invitee_bonus: 'Referral Bonus', // Legacy
  chest_inviter_bonus: 'Referral: Invitee Opened Chests',
  burn_bonus: 'Junk', // Legacy
  burn_token_bonus: 'Junk Tokens',
  burn_token_inviter_bonus: 'Referral: Invitee Junked Tokens',
  burn_nft_bonus: 'Junk NFT',
  burn_nft_inviter_bonus: 'Referral: Invitee Junked NFT',
  burn_mixed_bonus: 'Junk NFT and Tokens',
  burn_mixed_inviter_bonus: 'Referral: Invitee Junked NFT and Tokens',
  dust_swap_bonus: 'Dust Swap',
  dust_swap_inviter_bonus: 'Referral: Invitee Dust Swapped',
  referral_bonus: 'Referral',
  manual_adjustment: 'Adjustment',
  contribution: 'Lottery Contribution',
  payout: 'Lottery Payout',
  reversal: 'Reversal',
  social_task: 'Social Task',
  share_bonus: 'Share Bonus',
  backpack_campaign_bonus: 'Backpack Campaign Bonus',
  solflare_campaign_bonus: 'Solflare Campaign Bonus',
  magic_eden_campaign_bonus: '$ME Staker Bonus',
  referral_campaign_bonus: 'Referral Campaign Bonus',
  gate_wallet_campaign_bonus: 'Gate Wallet Bonus',
  gate_campaign_bonus: 'Gate Wallet Bonus',
  breakpoint_winner_campaign_bonus: 'Breakpoint Winner Bonus',
};

// Format timestamp for display
function formatTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) return '--';
  try {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return '--';
  }
}

export default function PointsPage() {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prizeTiersOpen, setPrizeTiersOpen] = useState(false);
  const [solflareIconHovered, setSolflareIconHovered] = useState(false);

  // Feature flag for new leaderboard types (burned, cumulative)
  const enableNewLeaderboards = config.ENABLE_NEW_LEADERBOARDS;

  // Always use 'all' range for leaderboard - no options needed
  const leaderboardRange: TimeRange = 'all';
  // Ranking type state - default to unused points
  const [rankingType, setRankingType] = useState<RankingType>('unused');

  const { isConnected } = useWalletAddress();
  const { isAuthenticated } = useAuthContext();
  const { userPoints } = useUserPoints();
  const { campaignData } = useSolflareCampaign();
  const { campaignData: magicEdenCampaignData } = useMagicEdenCampaign();
  const seasonQuery = usePointsLotterySeason();
  const season = seasonQuery.data?.season;
  const seasonError = seasonQuery.error;
  const isNoSeasonError =
    seasonError instanceof Error &&
    seasonError.message.toLowerCase().includes('no active season');

  // Fetch points ledger history with infinite scroll - 10 items per page
  const {
    data: ledgerData,
    isLoading: ledgerLoading,
    error: ledgerError,
    isError: ledgerIsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePointsLedgerInfinite({
    limit: 10,
    enabled: isConnected && isAuthenticated,
  });

  // Fetch leaderboard data with selected range and type - show top 200 users
  // Only fetch when feature is enabled or when on unused tab
  const shouldFetchLeaderboard = enableNewLeaderboards || rankingType === 'unused';
  const {
    data: leaderboardData,
    isLoading: leaderboardLoading,
    error: leaderboardError,
    isError: leaderboardIsError,
  } = usePointsLeaderboard({
    range: leaderboardRange,
    page: 1,
    limit: 200,
    type: rankingType,
    enabled: shouldFetchLeaderboard,
  });

  // Fetch current user's rank for all time ranges and selected type
  // Only fetch when feature is enabled or when on unused tab
  const shouldFetchRank = enableNewLeaderboards || rankingType === 'unused';
  const { data: myRankAll } = useMyPointsRank('all', rankingType, shouldFetchRank);
  const { data: myRank30d } = useMyPointsRank('30d', rankingType, shouldFetchRank);
  const { data: myRank7d } = useMyPointsRank('7d', rankingType, shouldFetchRank);
  const { data: myRank24h } = useMyPointsRank('24h', rankingType, shouldFetchRank);

  // Flatten all pages into a single array of items
  const allLedgerItems = ledgerData?.pages.flatMap(page => page.items) ?? [];

  const isSeasonActive = season && season.phase === 'active';

  const maxPossibleWinSOL = season?.maxPossibleWinLamports
    ? lamportsToSol(BigInt(season.maxPossibleWinLamports))
    : 0;

  // Live countdown that updates every second
  const [countdown, setCountdown] = useState('--');

  useEffect(() => {
    const updateCountdown = () => {
      setCountdown(
        season?.activeEndsAt ? formatCountdown(season.activeEndsAt) : '--'
      );
    };

    updateCountdown(); // Initial update
    const interval = setInterval(updateCountdown, 1000); // Update every second

    return () => clearInterval(interval);
  }, [season?.activeEndsAt]);

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          console.log(
            '[PointsPage] Load more trigger visible, fetching next page...'
          );
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div
      className="min-h-screen"
      style={{ position: 'relative', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <TopNav
        onHistoryClick={() => setHistoryOpen(true)}
        onFaqClick={() => setFaqOpen(true)}
        onPrizeTiersClick={() => setPrizeTiersOpen(true)}
        onSettingsClick={() => setSettingsOpen(true)}
        onNFTGalleryClick={() => navigate('/my-nft')}
      />

      <div className="px-4 py-8 pb-24">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* No Active Season Banner */}
          {isNoSeasonError && (
            <div
              className="relative overflow-hidden rounded-xl p-6"
              style={{
                border: '2px solid rgba(80, 228, 249, 0.3)',
                background:
                  'linear-gradient(90deg, rgba(81,228,248,0.1), rgba(1,74,92,0.4))',
              }}
            >
              <div className="text-center">
                <h2
                  className="mb-2 text-xl text-white md:text-2xl"
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    fontWeight: 700,
                  }}
                >
                  No Active Season
                </h2>
                <p
                  className="text-sm text-white/70"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}
                >
                  The Points Lottery is currently not active. A new season will
                  start soon. Check back later!
                </p>
              </div>
            </div>
          )}

          {/* Lottery Banner - Only shown when season is active */}
          {!isNoSeasonError && isSeasonActive && (
            <div
              className="relative overflow-hidden rounded-xl p-6 max-md:p-3"
              style={{
                border: '2px solid #C77DFF',
                boxShadow: '0 4px 20px rgba(157, 78, 221, 0.3)',
              }}
            >
              {/* Background Image */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: 'url(/lotterybanner.svg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: 1,
                }}
              />

              {/* Content */}
              <div className="relative z-10 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h2
                    className="overflow-hidden text-base text-ellipsis whitespace-nowrap text-white max-md:inline-flex max-md:flex-col md:text-2xl"
                    style={{
                      fontFamily: 'Hyperspace Race Capsule, cursive',
                      fontWeight: 700,
                    }}
                  >
                    Points Lottery Season {season?.seasonNo}{' '}
                    <span
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        background:
                          'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      WIN UP TO{' '}
                      {maxPossibleWinSOL.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 4,
                      })}{' '}
                      SOL
                    </span>{' '}
                    <span
                      className="text-white/90"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9em',
                      }}
                    >
                      End: {countdown}
                    </span>
                  </h2>
                </div>
                <button
                  onClick={() => navigate(AppRoutesEnum.Rewards)}
                  className="rounded-xl px-8 py-3 tracking-wide uppercase transition-all hover:scale-105"
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    fontWeight: 700,
                    fontSize: '18px',
                    background:
                      'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                    color: '#000000',
                    borderRadius: '100px',
                    boxShadow: '0 4px 12px rgba(251, 139, 0, 0.4)',
                  }}
                >
                  GO
                </button>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Your Points Card */}
              <div
                className="rounded-xl p-6"
                style={{
                  backgroundColor: 'rgba(26, 26, 26, 0.8)',
                  border: '2px solid #50E4F9',
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-4 flex items-center gap-2">
                      <h2
                        className="text-xl text-white"
                        style={{
                          fontFamily: 'Hyperspace Race Capsule, cursive',
                          fontWeight: 700,
                        }}
                      >
                        Your Points
                      </h2>
                      <SolflareBonusInfoTooltip />
                      <MagicEdenBonusInfoTooltip />
                      <GateBonusInfoTooltip />
                    </div>
                    <div
                      className="mb-2 text-5xl text-white"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {formatNumber(userPoints.total_point)}
                    </div>
                  </div>
                  {campaignData?.walletProvider === 'Solflare' &&
                    campaignData?.remainingBonus > 0 && (
                      <div
                        className="relative flex-shrink-0"
                        onMouseEnter={() => setSolflareIconHovered(true)}
                        onMouseLeave={() => setSolflareIconHovered(false)}
                      >
                        <img
                          src="/solflare2x.svg"
                          alt="Solflare 2x Points"
                          className="h-10 w-auto cursor-pointer"
                        />
                        {/* Hover Tooltip */}
                        {solflareIconHovered && (
                          <div
                            className="absolute top-full right-0 z-50 mt-2 w-72 rounded-xl p-4 shadow-2xl"
                            style={{
                              backgroundColor: '#000000',
                              border: '2px solid #FFEF46',
                              boxShadow: '0 0 20px rgba(255, 239, 70, 0.3)',
                            }}
                          >
                            <p
                              className="text-sm"
                              style={{
                                color: '#FFFFFF',
                                fontFamily: 'Outfit, sans-serif',
                                lineHeight: '1.6',
                              }}
                            >
                              Solflare users can earn 2x points on chest
                              openings, with up to 20,000 bonus points
                              available. Event ends:{' '}
                              <strong style={{ color: '#FFEF46' }}>
                                Friday, November 29 at 00:00 UTC
                              </strong>
                              .
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  {magicEdenCampaignData?.isMagicEdenStaker &&
                    magicEdenCampaignData?.remainingBonus > 0 && (
                      <div className="flex-shrink-0">
                        <div
                          className="flex items-center gap-1.5 px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: 'rgba(228, 37, 117, 0.15)',
                            border: '1px solid #E42575',
                          }}
                        >
                          <img
                            src="/Magic Eden.png"
                            alt="Magic Eden"
                            className="w-4 h-4 object-contain"
                          />
                          <span
                            className="text-sm font-bold"
                            style={{ color: '#E42575', fontFamily: 'Outfit, sans-serif' }}
                          >
                            $ME 2x
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* On mobile: Show Earn More Points section here */}
              {isMobile && <EarnPointsSection />}

              {/* History Card */}
              <div
                className="flex flex-col overflow-hidden rounded-xl"
                style={{
                  backgroundColor: 'rgba(26, 26, 26, 0.8)',
                  border: '2px solid #50E4F9',
                  height: '437px',
                }}
              >
                <div className="flex-shrink-0 p-6 pb-4">
                  <h2
                    className="text-xl text-white"
                    style={{
                      fontFamily: 'Hyperspace Race Capsule, cursive',
                      fontWeight: 700,
                    }}
                  >
                    History
                  </h2>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr
                        className="border-b border-white/10 text-left"
                        style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)' }}
                      >
                        <th
                          className="px-6 py-3 text-sm text-[#50E4F9]"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          Time
                        </th>
                        <th
                          className="px-6 py-3 text-sm text-[#50E4F9]"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          How to obtain
                        </th>
                        <th
                          className="px-6 py-3 text-right text-sm text-[#50E4F9]"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          Quantity
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {!isConnected ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center">
                            <div className="flex flex-col items-center gap-2">
                              <span
                                className="text-white/70"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                Connect your wallet to view history
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : !isAuthenticated ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center">
                            <div className="flex flex-col items-center gap-2">
                              <span
                                className="text-white/70"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                Sign in to view your points history
                              </span>
                              <span
                                className="text-xs text-white/50"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                (Check browser console for details)
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : ledgerLoading ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-6 py-8 text-center text-white/50"
                          >
                            <div className="flex items-center justify-center gap-2">
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#50E4F9] border-t-transparent"></div>
                              <span
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                Loading history...
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : ledgerIsError ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center">
                            <div className="flex flex-col items-center gap-2">
                              <span
                                className="text-red-400"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                Error loading history
                              </span>
                              <span
                                className="text-xs text-white/50"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                {ledgerError?.message || 'Unknown error'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : allLedgerItems.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-6 py-8 text-center text-white/50"
                          >
                            <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                              No points history yet
                            </span>
                          </td>
                        </tr>
                      ) : (
                        <>
                          {allLedgerItems.map(entry => (
                            <tr
                              key={entry.id}
                              className="border-b border-white/5 transition-colors hover:bg-white/5"
                            >
                              <td
                                className="px-6 py-3 text-sm text-white/70"
                                style={{
                                  fontFamily: 'Outfit, sans-serif',
                                  fontWeight: 400,
                                }}
                              >
                                {formatTimestamp(entry.createdAt)}
                              </td>
                              <td
                                className="px-6 py-3 text-sm text-white/90"
                                style={{
                                  fontFamily: 'Outfit, sans-serif',
                                  fontWeight: 500,
                                }}
                              >
                                {TYPE_LABELS[entry.type] || entry.type}
                              </td>
                              <td
                                className="px-6 py-3 text-right text-sm"
                                style={{
                                  fontFamily: 'Outfit, sans-serif',
                                  fontWeight: 700,
                                  color:
                                    Number(entry.pointsDelta) >= 0
                                      ? '#10b981'
                                      : '#ef4444',
                                }}
                              >
                                {Number(entry.pointsDelta) >= 0 ? '+' : ''}
                                {Number(entry.pointsDelta).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                          {/* Infinite scroll loading indicator */}
                          {isFetchingNextPage && (
                            <tr>
                              <td colSpan={3} className="px-6 py-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#50E4F9] border-t-transparent"></div>
                                  <span
                                    className="text-sm text-white/50"
                                    style={{ fontFamily: 'Outfit, sans-serif' }}
                                  >
                                    Loading more...
                                  </span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {/* Intersection observer trigger */}
                          {hasNextPage && !isFetchingNextPage && (
                            <tr>
                              <td colSpan={3} className="px-6 py-2">
                                <div
                                  ref={loadMoreRef}
                                  style={{ height: '20px' }}
                                />
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Right Column - Earn More Points (Desktop only) */}
            {!isMobile && (
              <div>
                <EarnPointsSection />
              </div>
            )}
          </div>

          {/* Leaderboard Section */}
          <div
            className="overflow-hidden rounded-xl"
            style={{
              backgroundColor: 'rgba(26, 26, 26, 0.8)',
              border: '2px solid #50E4F9',
            }}
          >
            {/* Header */}
            <div className="flex-shrink-0 p-3 pb-2 md:p-6 md:pb-4">
              <h2
                className="text-xl md:text-2xl text-white"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontWeight: 700,
                }}
              >
                Leaderboard
              </h2>
            </div>

            {/* Mobile: Horizontal Tabs */}
            <div className="md:hidden flex gap-2 px-3 pb-3 overflow-x-auto">
              <button
                onClick={() => setRankingType('burned')}
                className={`flex-shrink-0 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  rankingType === 'burned'
                    ? 'bg-[#50E4F9] text-black'
                    : 'bg-white/5 text-white/60'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Burned
              </button>
              <button
                onClick={() => setRankingType('cumulative')}
                className={`flex-shrink-0 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  rankingType === 'cumulative'
                    ? 'bg-[#50E4F9] text-black'
                    : 'bg-white/5 text-white/60'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Cumulative
              </button>
              <button
                onClick={() => setRankingType('unused')}
                className={`flex-shrink-0 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  rankingType === 'unused'
                    ? 'bg-[#50E4F9] text-black'
                    : 'bg-white/5 text-white/60'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Unused
              </button>
            </div>

            {/* Main Content: Tabs on left (desktop), table on right */}
            <div className="flex gap-4 px-3 md:px-6">
              {/* Left Sidebar - Ranking Type Tabs (desktop only) */}
              <div className="hidden md:flex flex-col gap-2" style={{ width: '200px' }}>
                <button
                  onClick={() => setRankingType('burned')}
                  className={`rounded-lg px-4 py-3 text-left text-sm font-medium transition-all ${
                    rankingType === 'burned'
                      ? 'bg-[#50E4F9] text-black'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Burned Points
                </button>
                <button
                  onClick={() => setRankingType('cumulative')}
                  className={`rounded-lg px-4 py-3 text-left text-sm font-medium transition-all ${
                    rankingType === 'cumulative'
                      ? 'bg-[#50E4F9] text-black'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Cumulative Points
                </button>
                <button
                  onClick={() => setRankingType('unused')}
                  className={`rounded-lg px-4 py-3 text-left text-sm font-medium transition-all ${
                    rankingType === 'unused'
                      ? 'bg-[#50E4F9] text-black'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Unused Points
                </button>
              </div>

              {/* Right Side - Leaderboard Table */}
              <div className="flex-1 overflow-hidden" style={{ maxHeight: '400px' }}>
                <div className="h-full overflow-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10" style={{ backgroundColor: 'rgba(26, 26, 26, 1)' }}>
                      <tr
                        className="border-b border-white/10 text-left"
                        style={{ backgroundColor: 'rgba(80, 228, 249, 0.15)' }}
                      >
                        <th
                          className="px-2 md:px-6 py-2 md:py-3 text-xs md:text-sm text-[#50E4F9]"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          Rank
                        </th>
                        <th
                          className="px-2 md:px-6 py-2 md:py-3 text-xs md:text-sm text-[#50E4F9]"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          Address
                        </th>
                        <th
                          className="px-2 md:px-6 py-2 md:py-3 text-right text-xs md:text-sm text-[#50E4F9]"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          <span className="hidden md:inline">
                            {rankingType === 'burned'
                              ? 'Burned Points'
                              : rankingType === 'cumulative'
                                ? 'Cumulative Points'
                                : 'Unused Points'}
                          </span>
                          <span className="md:hidden">Points</span>
                        </th>
                      </tr>
                    </thead>
                <tbody>
                  {/* Show Coming Soon for disabled features */}
                  {!enableNewLeaderboards && (rankingType === 'burned' || rankingType === 'cumulative') ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="text-2xl text-[#50E4F9]"
                            style={{
                              fontFamily: 'Hyperspace Race Capsule, cursive',
                              fontWeight: 700,
                            }}
                          >
                            Coming Soon
                          </div>
                          <p
                            className="text-sm text-white/60"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              fontWeight: 500,
                            }}
                          >
                            {rankingType === 'burned' ? 'Burned Points' : 'Cumulative Points'} leaderboard is not yet available
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : leaderboardLoading ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-8 text-center text-white/50"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#50E4F9] border-t-transparent"></div>
                          <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Loading leaderboard...
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : leaderboardIsError ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <span
                            className="text-red-400"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            Error loading leaderboard
                          </span>
                          <span
                            className="text-xs text-white/50"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            {leaderboardError?.message || 'Unknown error'}
                          </span>
                          <span
                            className="text-xs text-white/50"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            Check browser console for details
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : !leaderboardData?.items ||
                    leaderboardData.items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-8 text-center text-white/50"
                      >
                        <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                          No leaderboard data yet
                        </span>
                      </td>
                    </tr>
                  ) : (
                    leaderboardData.items.map(entry => (
                      <tr
                        key={entry.rank}
                        className="border-b border-white/5 transition-colors hover:bg-white/5"
                      >
                        <td
                          className="px-2 md:px-6 py-2 md:py-3 text-xs md:text-sm text-white/90"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 700,
                          }}
                        >
                          {entry.rank}
                        </td>
                        <td
                          className="px-2 md:px-6 py-2 md:py-3 text-xs md:text-sm text-white/90"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 500,
                          }}
                        >
                          <span className="hidden md:inline">{entry.address.slice(0, 6)}...{entry.address.slice(-4)}</span>
                          <span className="md:hidden">{entry.address.slice(0, 4)}...{entry.address.slice(-3)}</span>
                        </td>
                        <td
                          className="px-2 md:px-6 py-2 md:py-3 text-right text-xs md:text-sm text-white"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 700,
                          }}
                        >
                          {Number(entry.points).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

            {/* User Rank Information - Below leaderboard (hide for disabled feature types) */}
            {isConnected && (myRankAll || myRank30d || myRank7d || myRank24h) &&
             (enableNewLeaderboards || rankingType === 'unused') && (() => {
              // Temporary fix: When feature flag is disabled and showing unused points,
              // use actual total_point from dashboard instead of incomplete leaderboard data
              const useActualBalance = !enableNewLeaderboards && rankingType === 'unused';
              const displayPoints = useActualBalance ? userPoints.total_point : null;

              return (
              <div className="px-3 md:px-6 pb-4 md:pb-6 pt-3">
                <div className="mb-2">
                  <h2
                    className="text-base md:text-lg text-white"
                    style={{
                      fontFamily: 'Hyperspace Race Capsule, cursive',
                      fontWeight: 700,
                    }}
                  >
                    Your Rank Information
                  </h2>
                </div>

                {/* Grid of time ranges */}
                <div className="grid grid-cols-2 gap-2 md:gap-3 md:grid-cols-2 lg:grid-cols-4">
                  {/* All Time */}
                  <div
                    className="rounded-lg p-3 md:p-4"
                    style={{
                      backgroundColor: 'rgba(80, 228, 249, 0.1)',
                      border: '1px solid rgba(80, 228, 249, 0.3)',
                    }}
                  >
                    <div
                      className="mb-2 text-xs text-white/70"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      All Time
                    </div>
                    <div
                      className="mb-1 text-2xl text-white"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {myRankAll?.rank !== null && myRankAll?.rank !== undefined
                        ? `#${myRankAll.rank}`
                        : 'Unranked'}
                    </div>
                    <div
                      className="text-xs text-white/60"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      {Number(displayPoints !== null ? displayPoints : (myRankAll?.points || 0)).toLocaleString()} pts
                    </div>
                  </div>

                  {/* Last 30 Days */}
                  <div
                    className="rounded-lg p-3 md:p-4"
                    style={{
                      backgroundColor: 'rgba(80, 228, 249, 0.1)',
                      border: '1px solid rgba(80, 228, 249, 0.3)',
                    }}
                  >
                    <div
                      className="mb-2 text-xs text-white/70"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      Last 30 Days
                    </div>
                    <div
                      className="mb-1 text-2xl text-white"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {myRank30d?.rank !== null && myRank30d?.rank !== undefined
                        ? `#${myRank30d.rank}`
                        : 'Unranked'}
                    </div>
                    <div
                      className="text-xs text-white/60"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      {Number(myRank30d?.points || 0).toLocaleString()} pts
                    </div>
                  </div>

                  {/* Last 7 Days */}
                  <div
                    className="rounded-lg p-3 md:p-4"
                    style={{
                      backgroundColor: 'rgba(80, 228, 249, 0.1)',
                      border: '1px solid rgba(80, 228, 249, 0.3)',
                    }}
                  >
                    <div
                      className="mb-2 text-xs text-white/70"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      Last 7 Days
                    </div>
                    <div
                      className="mb-1 text-2xl text-white"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {myRank7d?.rank !== null && myRank7d?.rank !== undefined
                        ? `#${myRank7d.rank}`
                        : 'Unranked'}
                    </div>
                    <div
                      className="text-xs text-white/60"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      {Number(myRank7d?.points || 0).toLocaleString()} pts
                    </div>
                  </div>

                  {/* Last 24 Hours */}
                  <div
                    className="rounded-lg p-3 md:p-4"
                    style={{
                      backgroundColor: 'rgba(80, 228, 249, 0.1)',
                      border: '1px solid rgba(80, 228, 249, 0.3)',
                    }}
                  >
                    <div
                      className="mb-2 text-xs text-white/70"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      Last 24 Hours
                    </div>
                    <div
                      className="mb-1 text-2xl text-white"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {myRank24h?.rank !== null && myRank24h?.rank !== undefined
                        ? `#${myRank24h.rank}`
                        : 'Unranked'}
                    </div>
                    <div
                      className="text-xs text-white/60"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      {Number(myRank24h?.points || 0).toLocaleString()} pts
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}
          </div>
        </div>
      </div>

      <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <FaqDrawer open={faqOpen} onClose={() => setFaqOpen(false)} />
      <SettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      <PrizeTiersDrawer
        open={prizeTiersOpen}
        onClose={() => setPrizeTiersOpen(false)}
      />

      <BottomNavigation />
    </div>
  );
}
